import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { bondsAPI } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { toast } from "sonner";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  Building2,
  Percent,
  Download,
  File as FileIcon,
  Image as ImageIcon,
} from "lucide-react";

const BondDetails = () => {
  const { bondId } = useParams();
  const navigate = useNavigate();

  // Fetch bond data from API
  const {
    data: bondData,
    isPending: isLoading,
    error,
  } = useQuery({
    queryKey: ["bond", bondId],
    queryFn: () => bondsAPI.getBond(bondId),
    select: (data) => {
      // Handle the new response structure
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        return responseData.data.bond;
      }
      return null;
    },
    enabled: !!bondId,
  });

  // Fetch bond documents
  const { data: documentsData, isPending: isLoadingDocuments } = useQuery({
    queryKey: ["bondDocuments", bondId],
    queryFn: () => bondsAPI.getBondDocuments(bondId),
    select: (data) => {
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        return responseData.data.documents || [];
      }
      return [];
    },
    enabled: !!bondId,
  });

  // Handle loading state
  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading bond details..." />;
  }

  // Handle error state
  if (error) {
    console.error("Bond fetch error:", error);
    toast.error("Failed to load bond details");
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Loading Failed
          </h2>
          <p className="text-gray-600 mb-4">
            Unable to load bond details. Please try again later.
          </p>
          <button
            onClick={() => navigate("/issuer/bonds")}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Bond List
          </button>
        </div>
      </div>
    );
  }

  // Handle missing bond
  if (!bondData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Bond Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            The specified bond does not exist or has been deleted.
          </p>
          <button
            onClick={() => navigate("/issuer/bonds")}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Bond List
          </button>
        </div>
      </div>
    );
  }

  const bond = bondData;

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        className: "bg-success-100 text-success-800",
        icon: CheckCircle,
      },
      draft: {
        className: "bg-secondary-100 text-secondary-800",
        icon: FileText,
      },
      pending_review: {
        className: "bg-warning-100 text-warning-800",
        icon: Clock,
      },
      under_review: { className: "bg-info-100 text-info-800", icon: Info },
      approved: {
        className: "bg-success-100 text-success-800",
        icon: CheckCircle,
      },
      rejected: {
        className: "bg-error-100 text-error-800",
        icon: AlertTriangle,
      },
      listed: { className: "bg-success-100 text-success-800", icon: Building2 },
      redemption_in_process: {
        className: "bg-warning-100 text-warning-800",
        icon: Clock,
      },
      burn_in_process: {
        className: "bg-error-100 text-error-800",
        icon: AlertTriangle,
      },
      matured: {
        className: "bg-secondary-100 text-secondary-800",
        icon: Calendar,
      },
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig["draft"];
    const StatusIcon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.className}`}
      >
        <StatusIcon className="w-4 h-4 mr-1" />
        {status?.charAt(0).toUpperCase() + status?.slice(1).replace("_", " ")}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: bond.currency || "MYR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-MY", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return "N/A";
    return `${value}%`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith("image/")) {
      return <ImageIcon className="h-5 w-5 text-blue-500" />;
    }
    return <FileIcon className="h-5 w-5 text-gray-500" />;
  };

  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error("File URL not available");
      return;
    }

    // Open in new tab to trigger download
    window.open(fileUrl, "_blank");
  };

  const getDocumentCategoryLabel = (category) => {
    const categoryLabels = {
      riskAssessment: "Risk Assessment",
      legalDocuments: "Legal Documents",
      covenantsDocuments: "Covenants Documents",
      valuationReport: "Valuation Report",
      esgCertification: "ESG Certification",
      securitiesLodgementForm: "Securities Lodgement Form",
      globalCertificate: "Global Certificate",
      "payment-reference": "Payment Reference",
      shariahCertification: "Shariah Certification",
      shariahCompliance: "Shariah Compliance",
      underlyingAssetDocs: "Underlying Asset Documents",
    };

    return categoryLabels[category] || category;
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Summary widgets removed; consolidated into Key Information */}

      {/* Bond Description */}
      {bond.description && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Bond Description
          </h3>
          <p className="text-gray-700 leading-relaxed">{bond.description}</p>
        </div>
      )}

      {/* Key Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900">Key Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Bond Number
              </span>
              <span className="text-sm text-gray-900">
                {bond.bondNumber || "N/A"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">ISIN</span>
              <span className="text-sm text-gray-900">{bond.isin || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Stock Code
              </span>
              <span className="text-sm text-gray-900">
                {bond.stockCode || "-"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Bond Type
              </span>
              <span className="text-sm text-gray-900">{bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}</span>
            </div>
            {/* Removed: Currency, Quantity, Lock Period */}
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Total Issue Amount
              </span>
              <span className="text-sm text-gray-900">
                {formatCurrency(bond.issueAmount)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Coupon Rate
              </span>
              <span className="text-sm text-gray-900">
                {formatPercentage(bond.couponRate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Minimum Investment
              </span>
              <span className="text-sm text-gray-900">
                {formatCurrency(bond.minimumInvestmentValue)}
              </span>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Issue Date
              </span>
              <span className="text-sm text-gray-900">
                {formatDate(bond.issueDate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Maturity Date
              </span>
              <span className="text-sm text-gray-900">
                {formatDate(bond.maturityDate)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Coupon Frequency
              </span>
              <span className="text-sm text-gray-900">
                {bond.couponFrequency === "monthly" ||
                bond.couponFrequency === "Monthly"
                  ? "Monthly"
                  : bond.couponFrequency === "quarterly" ||
                    bond.couponFrequency === "Quarterly"
                  ? "Quarterly"
                  : bond.couponFrequency === "semi-annually" ||
                    bond.couponFrequency === "Semi-Annually"
                  ? "Semi-Annually"
                  : bond.couponFrequency === "annually" ||
                    bond.couponFrequency === "Annually"
                  ? "Annually"
                  : bond.couponFrequency === "at_maturity" ||
                    bond.couponFrequency === "At Maturity"
                  ? "At Maturity"
                  : bond.couponFrequency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">
                Current Status
              </span>
              <span className="text-sm text-gray-900">
                {getStatusBadge(bond.status)}
              </span>
            </div>
            {/* Removed: Funding Status, Total Funded Amount */}
            {bond.productType === "Sukuk" && bond.sukukDetails?.sukukType && (
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">
                  Sukuk Type
                </span>
                <span className="text-sm text-gray-900">
                  {bond.sukukDetails.sukukType.charAt(0).toUpperCase() +
                    bond.sukukDetails.sukukType.slice(1)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Token Details */}
      {bond.tokenDetails && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Token Information
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">
                  Token Name
                </span>
                <span className="text-sm text-gray-900">
                  {bond.tokenDetails.tokenName || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">
                  Token Symbol
                </span>
                <span className="text-sm text-gray-900">
                  {bond.tokenDetails.symbol || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">
                  Decimal Places
                </span>
                <span className="text-sm text-gray-900">
                  {bond.tokenDetails.decimal}
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">
                  Face Value per Token
                </span>
                <span className="text-sm text-gray-900">
                  {formatCurrency(bond.tokenDetails.faceValuePerToken)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-500">
                  Wallet Address
                </span>
                <span className="text-sm text-gray-900 font-mono text-xs">
                  {bond.tokenDetails.wallet || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bond Specific Details */}
      {bond.productType === "Sukuk" && bond.sukukDetails && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900">Sukuk Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {bond.sukukDetails.underlyingAsset && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Underlying Asset
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.sukukDetails.underlyingAsset}
                  </span>
                </div>
              )}
              {bond.sukukDetails.sukukStructure && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Structure
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.sukukDetails.sukukStructure}
                  </span>
                </div>
              )}
              {bond.sukukDetails.shariahAdvisor && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Shariah Advisor
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.sukukDetails.shariahAdvisor}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-4">
              {bond.sukukDetails.profitSharingRatio && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Profit Sharing Ratio
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.sukukDetails.profitSharingRatio}
                  </span>
                </div>
              )}
              {bond.sukukDetails.expectedReturn && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Expected Return
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.sukukDetails.expectedReturn}
                  </span>
                </div>
              )}
              {bond.sukukDetails.shariahCompliance && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Shariah Compliance
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.sukukDetails.shariahCompliance}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {bond.productType === "Fixed Notes" && bond.fixedNotesDetails && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              Conventional Details
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {bond.fixedNotesDetails.creditRating && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Credit Rating
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.fixedNotesDetails.creditRating}
                  </span>
                </div>
              )}
              {bond.fixedNotesDetails.guarantor && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Guarantor
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.fixedNotesDetails.guarantor}
                  </span>
                </div>
              )}
              {bond.fixedNotesDetails.collateral && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Collateral
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.fixedNotesDetails.collateral}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-4">
              {bond.fixedNotesDetails.useOfProceeds && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Use of Proceeds
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.fixedNotesDetails.useOfProceeds}
                  </span>
                </div>
              )}
              {bond.fixedNotesDetails.taxImplications && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">
                    Tax Implications
                  </span>
                  <span className="text-sm text-gray-900">
                    {bond.fixedNotesDetails.taxImplications}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Documents */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            Uploaded Documents
          </h3>
        </div>
        {isLoadingDocuments ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" text="Loading documents..." />
          </div>
        ) : documentsData && documentsData.length > 0 ? (
          <div className="space-y-3">
            {documentsData.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {getFileIcon(doc.mimeType)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.originalName || doc.name}
                      </p>
                      {doc.metadata?.category && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getDocumentCategoryLabel(doc.metadata.category)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-xs text-gray-500">
                        {formatFileSize(doc.size)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {doc.mimeType}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(doc.uploadedAt)}
                      </span>
                    </div>
                  </div>
                </div>
                {doc.fileUrl && (
                  <button
                    onClick={() =>
                      handleDownloadFile(
                        doc.fileUrl,
                        doc.originalName || doc.name
                      )
                    }
                    className="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No documents uploaded yet</p>
          </div>
        )}
      </div>
    </div>
  );

  // Removed Performance and Timeline tabs per request

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate("/issuer/bonds")}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {bond.bondName}
              </h1>
            </div>
            {/* Removed top-right status badge; status shown in Key Information */}
          </div>
        </div>

        {/* Tabs removed: only Overview is shown */}

        {/* Content */}
        <div className="mb-8">{renderOverviewTab()}</div>
      </div>
    </div>
  );
};

export default BondDetails;
